import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  MdBolt,
  MdPeopleOutline,
  MdVisibility,
  MdPlayCircleOutline,
  MdLogin,
  MdRestartAlt,
  MdLeaderboard,
} from 'react-icons/md';
import { apiGet } from 'utils/apiFetch';
import apiPath from 'utils/apiPath';
import useToastContext from 'hooks/useToastContext';
import ActivityNav from './ActivityNav';
import {
  BRAND,
  BRAND_HOVER,
  BRAND_SOFT,
  BREAKDOWN_COLORS,
  PERIOD_OPTIONS,
} from './constants';
import {
  agentDisplayName,
  avatarColorFromId,
  chartLabel,
  formatLiveStamp,
  formatTrend,
  getInitials,
  parsePeriodDays,
} from './utils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const STAT_META = [
  {
    key: 'totalActivities',
    label: 'Total activities',
    icon: MdBolt,
    trendKey: 'totalActivities',
  },
  {
    key: 'activeAgents',
    label: 'Active agents',
    icon: MdPeopleOutline,
    trendKey: 'activeAgents',
  },
  {
    key: 'views',
    label: 'Property/unit views',
    icon: MdVisibility,
    trendKey: 'views',
  },
  {
    key: 'presentations',
    label: 'Presentations',
    icon: MdPlayCircleOutline,
    trendKey: 'presentations',
  },
  {
    key: 'loginsToday',
    label: 'Logins today',
    icon: MdLogin,
    trendKey: 'loginsToday',
  },
];

function Overview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const periodDays = parsePeriodDays(searchParams.get('periodDays'), 30);
  const navigate = useNavigate();
  const notification = useToastContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const setPeriod = (days) => {
    const next = new URLSearchParams(searchParams);
    next.set('periodDays', String(days));
    setSearchParams(next);
  };

  const resetFilters = () => {
    setSearchParams({});
  };

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiGet(apiPath.activityOverview, { periodDays });
      if (res?.data?.success) {
        setData(res.data.results);
      } else {
        notification.error(res?.data?.message || 'Unable to load overview.');
      }
    } catch (error) {
      notification.error(
        error?.response?.data?.message || 'Unable to load overview.'
      );
    } finally {
      setLoading(false);
    }
  }, [notification, periodDays]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const chartData = useMemo(() => {
    const series = data?.series || [];
    return {
      labels: series.map((s, i) => chartLabel(s.date, i, series.length)),
      datasets: [
        {
          label: 'Total activities',
          data: series.map((s) => s.count),
          borderColor: BRAND,
          backgroundColor: BRAND_SOFT,
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
      ],
    };
  }, [data]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { maxRotation: 0, color: '#9CA3AF', font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: '#F3F4F6' },
          ticks: { color: '#9CA3AF', font: { size: 11 }, precision: 0 },
        },
      },
    }),
    []
  );

  return (
    <div className="px-4 lg:px-7 py-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 m-0">
            Agent Activity Tracker
          </h1>
          <p className="text-sm text-gray-500 mt-1 mb-0">
            Monitor engagement and performance across all agents.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{
              background: BRAND,
              boxShadow: `0 0 0 3px ${BRAND_SOFT}`,
            }}
          />
          Live · {formatLiveStamp(data?.updatedAt)}
        </div>
      </div>

      <ActivityNav />

      <Card
        variant="outlined"
        sx={{ mb: 2.5, borderRadius: 2, borderColor: '#E5E7EB' }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            py: '14px !important',
          }}
        >
          <div
            className="inline-flex p-1 rounded-lg"
            style={{ background: '#F3F4F6' }}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPeriod(opt.value)}
                className="px-3 py-1.5 text-sm rounded-md border-0 cursor-pointer"
                style={{
                  background: periodDays === opt.value ? '#fff' : 'transparent',
                  color: periodDays === opt.value ? '#111827' : '#6B7280',
                  fontWeight: periodDays === opt.value ? 600 : 400,
                  boxShadow:
                    periodDays === opt.value
                      ? '0 1px 2px rgba(0,0,0,0.06)'
                      : 'none',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <Button
            startIcon={<MdRestartAlt />}
            onClick={resetFilters}
            sx={{
              color: '#6B7280',
              textTransform: 'none',
              ml: 'auto',
              '&:hover': { color: BRAND_HOVER, background: BRAND_SOFT },
            }}
          >
            Reset
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-4">
        {STAT_META.map((stat) => {
          const Icon = stat.icon;
          const value = data?.summary?.[stat.key] ?? '—';
          const trend = data?.trends?.[stat.trendKey];
          return (
            <Card
              key={stat.key}
              variant="outlined"
              sx={{ borderRadius: 2, borderColor: '#E5E7EB' }}
            >
              <CardContent sx={{ py: '16px !important' }}>
                <div className="flex items-center justify-between mb-2">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      background: BRAND_SOFT,
                      color: BRAND,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={18} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: Number(trend) >= 0 ? BRAND : '#EF4444',
                    }}
                  >
                    {formatTrend(trend)}
                  </Typography>
                </div>
                <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
                  {loading ? '…' : value}
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.5 }}>
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 mb-4">
        <Card
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: '#E5E7EB',
            gridColumn: { xl: 'span 2' },
          }}
        >
          <CardContent>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              Activity over time
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 2 }}>
              Total activities · last {periodDays} days
            </Typography>
            <Box sx={{ height: 260 }}>
              <Line data={chartData} options={chartOptions} />
            </Box>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{ borderRadius: 2, borderColor: '#E5E7EB' }}
        >
          <CardContent>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>
              Breakdown by type
            </Typography>
            <div className="flex flex-col gap-4">
              {(data?.breakdown || []).map((item) => (
                <div key={item.category}>
                  <div className="flex items-center justify-between mb-1">
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                      {item.count} ({item.percent}%)
                    </Typography>
                  </div>
                  <LinearProgress
                    variant="determinate"
                    value={item.percent}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#F3F4F6',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor:
                          BREAKDOWN_COLORS[item.category] || BRAND,
                        borderRadius: 4,
                      },
                    }}
                  />
                </div>
              ))}
              {!data?.breakdown?.length && !loading && (
                <Typography sx={{ fontSize: 13, color: '#9CA3AF' }}>
                  No activity in this period.
                </Typography>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E5E7EB' }}>
        <CardContent>
          <div className="flex items-center gap-2 mb-1">
            <MdLeaderboard size={20} color={BRAND} />
            <Typography sx={{ fontWeight: 700 }}>Agent leaderboard</Typography>
          </div>
          <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 2 }}>
            Ranked by total activity · last {periodDays} days
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#9CA3AF', fontWeight: 600 }}>
                  RANK
                </TableCell>
                <TableCell sx={{ color: '#9CA3AF', fontWeight: 600 }}>
                  AGENT
                </TableCell>
                <TableCell sx={{ color: '#9CA3AF', fontWeight: 600 }} align="right">
                  VIEWS
                </TableCell>
                <TableCell sx={{ color: '#9CA3AF', fontWeight: 600 }} align="right">
                  ENGAGE
                </TableCell>
                <TableCell sx={{ color: '#9CA3AF', fontWeight: 600 }} align="right">
                  PRESENT
                </TableCell>
                <TableCell sx={{ color: '#9CA3AF', fontWeight: 600 }}>
                  TOTAL ACTIVITY
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.leaderboard || []).map((row) => (
                <TableRow key={row.agentId} hover>
                  <TableCell
                    sx={{
                      color: row.rank <= 3 ? BRAND : '#6B7280',
                      fontWeight: 700,
                    }}
                  >
                    {row.rank}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar
                        src={row.profilePic || undefined}
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: 13,
                          bgcolor: avatarColorFromId(row.agentId),
                        }}
                      >
                        {getInitials(row.firstName, row.lastName)}
                      </Avatar>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {agentDisplayName(row)}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell align="right">{row.views}</TableCell>
                  <TableCell align="right">{row.engage}</TableCell>
                  <TableCell align="right">{row.present}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <div className="flex items-center gap-2">
                      <LinearProgress
                        variant="determinate"
                        value={row.barPercent || 0}
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#F3F4F6',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: BRAND,
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography sx={{ fontWeight: 600, fontSize: 13, minWidth: 28 }}>
                        {row.total}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      onClick={() =>
                        navigate(
                          `/activity-tracker/agents/${row.agentId}?periodDays=${periodDays}`
                        )
                      }
                      sx={{
                        color: BRAND,
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { background: BRAND_SOFT },
                      }}
                    >
                      View &gt;
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!data?.leaderboard?.length && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#9CA3AF' }}>
                    No agent activity for this period.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default Overview;
